import React, { useEffect, useState } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import MarkerClusterGroup from 'react-leaflet-cluster';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import styled from 'styled-components';
import { MapPin, Users, Target, X, Check, UserPlus } from 'lucide-react';

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

const MapWrapper = styled.div`
  background: linear-gradient(135deg, var(--card-bg) 0%, var(--bg) 100%);
  border: 1px solid var(--card-border);
  border-radius: 12px;
  padding: 20px;
  margin-bottom: 30px;
  box-shadow: 0 4px 16px rgba(165, 145, 239, 0.4);
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: linear-gradient(90deg, transparent, rgba(165, 145, 239, 0.6), transparent);
  }

  .leaflet-container {
    height: 500px;
    border-radius: 8px;
    z-index: 1;
  }

  .leaflet-popup-content-wrapper {
    background: var(--card-bg);
    color: white;
    border-radius: 8px;
    border: 1px solid var(--primary);
  }

  .leaflet-popup-tip {
    background: var(--card-bg);
  }
`;

const MapHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
`;

const MapTitle = styled.h2`
  color: var(--primary);
  font-size: 20px;
  font-weight: 700;
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 0;
`;

const MapStats = styled.div`
  display: flex;
  gap: 15px;
`;

const StatBadge = styled.div`
  background: var(--card-bg);
  padding: 8px 14px;
  border-radius: 6px;
  border: 1px solid var(--card-border);
  color: var(--text-secondary);
  font-weight: 600;
  font-size: 14px;
  display: flex;
  align-items: center;
  gap: 6px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: var(--surface);
    border-color: var(--primary);
    color: var(--text-primary);
  }
`;

const PopupContent = styled.div`
  padding: 8px;
  min-width: 200px;
`;

const PopupUsername = styled.a`
  color: var(--primary);
  font-weight: 700;
  font-size: 16px;
  text-decoration: none;
  display: block;
  margin-bottom: 8px;

  &:hover {
    color: var(--secondary);
  }
`;

const PopupInfo = styled.div`
  color: var(--text-secondary);
  font-size: 13px;
  margin-bottom: 4px;
  display: flex;
  align-items: center;
  gap: 6px;
`;

const PopupLocation = styled.div`
  color: #fff;
  font-size: 14px;
  font-weight: 600;
  margin-bottom: 8px;
  display: flex;
  align-items: center;
  gap: 6px;
`;

const MapContentGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 20px;
`;

const MapSection = styled.div`
  .leaflet-container {
    height: 550px;
    border-radius: 8px;
    z-index: 1;
  }
`;

const FilterDropdown = styled.div`
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  background: var(--card-bg);
  border: 1px solid var(--card-border);
  border-radius: 8px;
  padding: 12px;
  min-width: 300px;
  max-height: 400px;
  overflow-y: auto;
  z-index: 1000;
  box-shadow: 0 8px 24px rgba(165, 145, 239, 0.5);

  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-track {
    background: var(--bg);
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb {
    background: var(--card-border);
    border-radius: 4px;

    &:hover {
      background: var(--primary);
    }
  }
`;

const FilterTitle = styled.div`
  color: var(--text-primary);
  font-size: 14px;
  font-weight: 700;
  margin-bottom: 12px;
  padding-bottom: 8px;
  border-bottom: 1px solid var(--card-border);
`;

const FilterItem = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px;
  margin-bottom: 4px;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s ease;
  color: var(--text-secondary);
  font-size: 13px;

  &:hover {
    background: var(--surface);
    color: var(--text-primary);
  }

  input[type="checkbox"] {
    width: 16px;
    height: 16px;
    cursor: pointer;
  }
`;

const FilterActions = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid var(--card-border);
`;

const FilterButton = styled.button`
  flex: 1;
  padding: 8px 12px;
  background: ${props => props.$primary ? 'var(--primary)' : 'var(--card-bg)'};
  border: 1px solid ${props => props.$primary ? 'var(--primary)' : 'var(--card-border)'};
  border-radius: 6px;
  color: var(--text-primary);
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: ${props => props.$primary ? 'var(--secondary)' : 'var(--surface)'};
  }
`;

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
  backdrop-filter: blur(4px);
`;

const ModalContent = styled.div`
  background: var(--card-bg);
  border: 1px solid var(--card-border);
  border-radius: 12px;
  width: 90%;
  max-width: 800px;
  max-height: 80vh;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  box-shadow: 0 20px 60px rgba(165, 145, 239, 0.5);
`;

const ModalHeader = styled.div`
  padding: 20px;
  border-bottom: 1px solid var(--card-border);
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const ModalTitle = styled.h2`
  color: var(--text-primary);
  font-size: 20px;
  font-weight: 700;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 10px;
`;

const CloseButton = styled.button`
  background: transparent;
  border: none;
  color: var(--text-secondary);
  cursor: pointer;
  padding: 8px;
  border-radius: 6px;
  transition: all 0.2s ease;

  &:hover {
    background: var(--surface);
    color: var(--text-primary);
  }
`;

const ModalBody = styled.div`
  padding: 20px;
  overflow-y: auto;
  flex: 1;

  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-track {
    background: var(--bg);
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb {
    background: var(--card-border);
    border-radius: 4px;

    &:hover {
      background: var(--primary);
    }
  }
`;

const ModalFooter = styled.div`
  padding: 20px;
  border-top: 1px solid var(--card-border);
  display: flex;
  gap: 12px;
  justify-content: flex-end;
`;

const ProfileItem = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  background: var(--bg);
  border: 1px solid var(--card-bg);
  border-radius: 8px;
  margin-bottom: 8px;
  transition: all 0.2s ease;

  &:hover {
    background: var(--surface);
    border-color: var(--card-border);
  }
`;

const ProfileCheckbox = styled.input`
  width: 18px;
  height: 18px;
  cursor: pointer;
`;

const ProfileInfo = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const ProfileUsername = styled.a`
  color: var(--primary);
  font-weight: 700;
  font-size: 14px;
  text-decoration: none;

  &:hover {
    color: #7289da;
    text-decoration: underline;
  }
`;

const ProfileDetails = styled.div`
  color: var(--text-secondary);
  font-size: 12px;
  display: flex;
  gap: 12px;
`;

const ActionButton = styled.button`
  padding: 10px 20px;
  background: ${props => props.$primary ? 'var(--primary)' : 'var(--card-bg)'};
  border: 1px solid ${props => props.$primary ? 'var(--primary)' : 'var(--card-border)'};
  border-radius: 8px;
  color: var(--text-primary);
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 8px;

  &:hover {
    background: ${props => props.$primary ? 'var(--secondary)' : 'var(--surface)'};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

// Composant pour capturer la référence de la carte
function MapRefHandler({ setMapRef }) {
  const map = useMap();
  
  useEffect(() => {
    setMapRef(map);
  }, [map, setMapRef]);
  
  return null;
}

function MapView({ profiles }) {
  const [locationData, setLocationData] = useState([]);
  const [allLocationData, setAllLocationData] = useState([]);
  const [, setUnfilteredLocationData] = useState([]);
  const [stats, setStats] = useState({ total: 0, locations: 0, countries: 0 });
  const [countryStats, setCountryStats] = useState([]);
  const [showCountryFilter, setShowCountryFilter] = useState(false);
  const [showCityFilter, setShowCityFilter] = useState(false);
  const [selectedCountries, setSelectedCountries] = useState([]);
  const [selectedCities, setSelectedCities] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [modalLocation, setModalLocation] = useState(null);
  const [selectedProfiles, setSelectedProfiles] = useState([]);
  const [mapRef, setMapRef] = useState(null);
  const [modalCityFilter, setModalCityFilter] = useState('');
  const [modalSelectedCities, setModalSelectedCities] = useState([]);

  // Fonction pour corriger les incohérences de localisation
  const correctLocation = (city, country) => {
    // Villes françaises mal attribuées
    const frenchCities = ['Metz', 'Thionville', 'Nancy', 'Strasbourg', 'Lyon', 'Marseille', 
                          'Toulouse', 'Nice', 'Nantes', 'Bordeaux', 'Lille', 'Rennes', 
                          'Reims', 'Le Havre', 'Saint-Étienne', 'Toulon', 'Grenoble', 
                          'Dijon', 'Angers', 'Nîmes', 'Villeurbanne', 'Paris', 'Argenteuil', 
                          'Arnouville', 'Médoc'];
    
    if (city && frenchCities.includes(city) && country !== 'France') {
      return { city, country: 'France' };
    }
    
    // Ville de Luxembourg
    if (city === 'Luxembourg' && (!country || country === 'Luxembourg')) {
      return { city: 'Luxembourg', country: 'Luxembourg' };
    }
    
    return { city, country };
  };

  useEffect(() => {
    const countriesSet = new Set();
    const citiesMap = new Map();
    const countryCountMap = new Map();

    profiles.forEach(profile => {
      if (profile.location_country || profile.location_city) {
        // Corriger les incohérences de localisation
        const corrected = correctLocation(profile.location_city, profile.location_country);
        const city = corrected.city || profile.location_city;
        const country = corrected.country || profile.location_country;
        
        const key = `${city || 'Unknown'}-${country || 'Unknown'}`;
        
        if (!citiesMap.has(key)) {
          citiesMap.set(key, {
            city: city || 'Ville inconnue',
            country: country || 'Pays inconnu',
            profiles: [],
            coordinates: getCoordinates(city, country)
          });
        }
        
        citiesMap.get(key).profiles.push(profile);
        
        if (country) {
          countriesSet.add(country);
          countryCountMap.set(
            country,
            (countryCountMap.get(country) || 0) + 1
          );
        }
      }
    });

    const locations = Array.from(citiesMap.values()).filter(loc => loc.coordinates);
    const countriesArray = Array.from(countryCountMap.entries())
      .map(([country, count]) => ({ country, count }))
      .sort((a, b) => b.count - a.count);
    
    setUnfilteredLocationData(locations);
    setAllLocationData(locations);
    setCountryStats(countriesArray);
    
    // Appliquer les filtres
    let filteredLocations = locations;
    if (selectedCountries.length > 0) {
      filteredLocations = filteredLocations.filter(loc => selectedCountries.includes(loc.country));
    }
    if (selectedCities.length > 0) {
      filteredLocations = filteredLocations.filter(loc => selectedCities.includes(loc.city));
    }
    setLocationData(filteredLocations);
    setStats({
      total: profiles.filter(p => p.location_country || p.location_city).length,
      locations: locations.length,
      countries: countriesSet.size
    });
  }, [profiles, selectedCountries, selectedCities]);

  const toggleCountryFilter = (country) => {
    setSelectedCountries(prev => {
      if (prev.includes(country)) {
        return prev.filter(c => c !== country);
      } else {
        return [...prev, country];
      }
    });
  };

  const toggleCityFilter = (city) => {
    setSelectedCities(prev => {
      if (prev.includes(city)) {
        return prev.filter(c => c !== city);
      } else {
        return [...prev, city];
      }
    });
  };

  const selectAllCountries = () => {
    setSelectedCountries(countryStats.map(s => s.country));
  };

  const clearAllCountries = () => {
    setSelectedCountries([]);
  };

  const selectAllCities = () => {
    setSelectedCities(allLocationData.map(loc => loc.city));
  };

  const clearAllCities = () => {
    setSelectedCities([]);
  };

  const openLocationModal = (location) => {
    setModalLocation(location);
    setSelectedProfiles([]);
    setModalCityFilter('');
    setModalSelectedCities([]);
    setShowModal(true);
    
    // Zoomer sur la localisation
    if (mapRef && location.coordinates) {
      mapRef.setView(location.coordinates, 12, {
        animate: true,
        duration: 1
      });
    }
  };

  const closeModal = () => {
    setShowModal(false);
    setModalLocation(null);
    setSelectedProfiles([]);
    setModalCityFilter('');
    setModalSelectedCities([]);
  };

  const toggleProfileSelection = (profile) => {
    setSelectedProfiles(prev => {
      const isSelected = prev.some(p => p.username === profile.username);
      if (isSelected) {
        return prev.filter(p => p.username !== profile.username);
      } else {
        return [...prev, profile];
      }
    });
  };

  const selectAllProfiles = () => {
    if (modalLocation) {
      setSelectedProfiles(modalLocation.profiles);
    }
  };

  const deselectAllProfiles = () => {
    setSelectedProfiles([]);
  };

  const addAsTargets = async () => {
    if (selectedProfiles.length === 0) {
      alert('⚠️ Veuillez sélectionner au moins un profil');
      return;
    }

    try {
      const response = await fetch('/api/targets/bulk', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          profiles: selectedProfiles.map(p => ({
            username: p.username,
            full_name: p.full_name,
            profile_pic_url: p.profile_pic_url,
            followers_count: p.followers_count,
            following_count: p.following_count
          }))
        })
      });

      if (response.ok) {
        alert(`✅ ${selectedProfiles.length} profil(s) ajouté(s) comme cibles`);
        closeModal();
      } else {
        alert('❌ Erreur lors de l\'ajout des cibles');
      }
    } catch (error) {
      console.error('Erreur:', error);
      alert('❌ Erreur lors de l\'ajout des cibles');
    }
  };

  const getCountryCode = (country) => {
    if (!country) return 'XX';
    
    const codes = {
      'France': 'FR',
      'United Kingdom': 'GB',
      'United States': 'US',
      'États-Unis': 'US',
      'Japan': 'JP',
      'Germany': 'DE',
      'Spain': 'ES',
      'Italy': 'IT',
      'Italie': 'IT',
      'Netherlands': 'NL',
      'Belgium': 'BE',
      'Switzerland': 'CH',
      'Austria': 'AT',
      'Sweden': 'SE',
      'Denmark': 'DK',
      'Norway': 'NO',
      'Finland': 'FI',
      'Poland': 'PL',
      'Czech Republic': 'CZ',
      'Hungary': 'HU',
      'Greece': 'GR',
      'Portugal': 'PT',
      'Ireland': 'IE',
      'Canada': 'CA',
      'Australia': 'AU',
      'Singapore': 'SG',
      'United Arab Emirates': 'AE',
      'Émirats arabes unis': 'AE',
      'India': 'IN',
      'Inde': 'IN',
      'China': 'CN',
      'Chine': 'CN',
      'South Korea': 'KR',
      'Thailand': 'TH',
      'Mexico': 'MX',
      'Mexique': 'MX',
      'Brazil': 'BR',
      'Brésil': 'BR',
      'Argentina': 'AR',
      'Russia': 'RU',
      'Turkey': 'TR',
      'Egypt': 'EG',
      'Égypte': 'EG',
      'Nigeria': 'NG',
      'South Africa': 'ZA',
      'Morocco': 'MA',
      'Maroc': 'MA',
      'Tunisia': 'TN',
      'Tunisie': 'TN',
      'Algeria': 'DZ',
      'Algérie': 'DZ',
      'Senegal': 'SN',
      'Sénégal': 'SN',
      "Côte d'Ivoire": 'CI',
      'Kenya': 'KE',
      'Luxembourg': 'LU',
      'Vietnam': 'VN',
    };
    
    return codes[country?.trim()] || 'XX';
  };

  const FlagIcon = ({ countryCode }) => {
    return (
      <img 
        src={`https://flagcdn.com/24x18/${countryCode.toLowerCase()}.png`}
        srcSet={`https://flagcdn.com/48x36/${countryCode.toLowerCase()}.png 2x, https://flagcdn.com/72x54/${countryCode.toLowerCase()}.png 3x`}
        width="24"
        height="18"
        alt={countryCode}
        style={{ marginRight: '8px', borderRadius: '2px' }}
      />
    );
  };

  const getCoordinates = (city, country) => {
    // Coordonnées des centres de pays (fallback si ville inconnue)
    const countryCoordinates = {
      'France': [46.2276, 2.2137],
      'United Kingdom': [55.3781, -3.4360],
      'United States': [37.0902, -95.7129],
      'Japan': [36.2048, 138.2529],
      'Germany': [51.1657, 10.4515],
      'Spain': [40.4637, -3.7492],
      'Italy': [41.8719, 12.5674],
      'Netherlands': [52.1326, 5.2913],
      'Belgium': [50.5039, 4.4699],
      'Switzerland': [46.8182, 8.2275],
      'Austria': [47.5162, 14.5501],
      'Sweden': [60.1282, 18.6435],
      'Denmark': [56.2639, 9.5018],
      'Norway': [60.4720, 8.4689],
      'Finland': [61.9241, 25.7482],
      'Poland': [51.9194, 19.1451],
      'Czech Republic': [49.8175, 15.4730],
      'Hungary': [47.1625, 19.5033],
      'Greece': [39.0742, 21.8243],
      'Portugal': [39.3999, -8.2245],
      'Ireland': [53.1424, -7.6921],
      'Canada': [56.1304, -106.3468],
      'Australia': [-25.2744, 133.7751],
      'Singapore': [1.3521, 103.8198],
      'United Arab Emirates': [23.4241, 53.8478],
      'India': [20.5937, 78.9629],
      'China': [35.8617, 104.1954],
      'South Korea': [35.9078, 127.7669],
      'Thailand': [15.8700, 100.9925],
      'Mexico': [23.6345, -102.5528],
      'Brazil': [-14.2350, -51.9253],
      'Argentina': [-38.4161, -63.6167],
      'Russia': [61.5240, 105.3188],
      'Turkey': [38.9637, 35.2433],
      'Egypt': [26.8206, 30.8025],
      'Nigeria': [9.0820, 8.6753],
      'South Africa': [-30.5595, 22.9375],
      'Morocco': [31.7917, -7.0926],
      'Tunisia': [33.8869, 9.5375],
      'Algeria': [28.0339, 1.6596],
      'Senegal': [14.4974, -14.4524],
      "Côte d'Ivoire": [7.5400, -5.5471],
      'Kenya': [-0.0236, 37.9062],
      'Luxembourg': [49.8153, 6.1296],
      'Vietnam': [14.0583, 108.2772],
      'Malaysia': [4.2105, 101.9758],
      'Chile': [-35.6751, -71.5430]
    };
    
    const cityCoordinates = {
      'Paris': [48.8566, 2.3522],
      'Metz': [49.1193, 6.1757],
      'Thionville': [49.3586, 6.1686],
      'Nancy': [48.6921, 6.1844],
      'Strasbourg': [48.5734, 7.7521],
      'Lyon': [45.7640, 4.8357],
      'Marseille': [43.2965, 5.3698],
      'Toulouse': [43.6047, 1.4442],
      'Nice': [43.7102, 7.2620],
      'Nantes': [47.2184, -1.5536],
      'Bordeaux': [44.8378, -0.5792],
      'Lille': [50.6292, 3.0573],
      'Rennes': [48.1173, -1.6778],
      'Reims': [49.2583, 4.0317],
      'Le Havre': [49.4944, 0.1079],
      'Saint-Étienne': [45.4397, 4.3872],
      'Toulon': [43.1242, 5.9280],
      'Grenoble': [45.1885, 5.7245],
      'Dijon': [47.3220, 5.0415],
      'Angers': [47.4784, -0.5632],
      'Nîmes': [43.8367, 4.3601],
      'Villeurbanne': [45.7667, 4.8833],
      'Argenteuil': [48.9475, 2.2464],
      'Arnouville': [48.9886, 2.4178],
      'Médoc': [45.3167, -0.8000],
      'London': [51.5074, -0.1278],
      'New York': [40.7128, -74.0060],
      'Tokyo': [35.6762, 139.6503],
      'Berlin': [52.5200, 13.4050],
      'Madrid': [40.4168, -3.7038],
      'Rome': [41.9028, 12.4964],
      'Amsterdam': [52.3676, 4.9041],
      'Brussels': [50.8503, 4.3517],
      'Zurich': [47.3769, 8.5417],
      'Vienna': [48.2082, 16.3738],
      'Stockholm': [59.3293, 18.0686],
      'Copenhagen': [55.6761, 12.5683],
      'Oslo': [59.9139, 10.7522],
      'Helsinki': [60.1699, 24.9384],
      'Warsaw': [52.2297, 21.0122],
      'Prague': [50.0755, 14.4378],
      'Budapest': [47.4979, 19.0402],
      'Athens': [37.9838, 23.7275],
      'Lisbon': [38.7223, -9.1393],
      'Dublin': [53.3498, -6.2603],
      'Toronto': [43.6532, -79.3832],
      'Sydney': [-33.8688, 151.2093],
      'Dubai': [25.2048, 55.2708],
      'Mumbai': [19.0760, 72.8777],
      'Beijing': [39.9042, 116.4074],
      'Seoul': [37.5665, 126.9780],
      'Bangkok': [13.7563, 100.5018],
      'Mexico City': [19.4326, -99.1332],
      'São Paulo': [-23.5505, -46.6333],
      'Buenos Aires': [-34.6037, -58.3816],
      'Moscow': [55.7558, 37.6173],
      'Istanbul': [41.0082, 28.9784],
      'Cairo': [30.0444, 31.2357],
      'Lagos': [6.5244, 3.3792],
      'Cape Town': [-33.9249, 18.4241],
      'Johannesburg': [-26.2041, 28.0473],
      'Casablanca': [33.5731, -7.5898],
      'Tunis': [36.8065, 10.1815],
      'Algiers': [36.7538, 3.0588],
      'Dakar': [14.7167, -17.4677],
      'Abidjan': [5.3600, -4.0083],
      'Nairobi': [-1.2864, 36.8172],
      'Hanoi': [21.0285, 105.8542],
      'Kuala Lumpur': [3.1390, 101.6869],
      'Santiago': [-33.4489, -70.6693]
    };

    const cityKey = city ? city.trim() : null;
    const countryKey = country ? country.trim() : null;

    // Essayer d'abord avec la ville
    if (cityKey && cityCoordinates[cityKey]) {
      return cityCoordinates[cityKey];
    }
    
    // Sinon, utiliser le centre du pays
    if (countryKey && countryCoordinates[countryKey]) {
      return countryCoordinates[countryKey];
    }

    return null;
  };

  const createCustomIcon = (count) => {
    const size = Math.min(40 + count * 2, 60);
    return L.divIcon({
      html: `
        <div style="
          background: linear-gradient(135deg, var(--primary), var(--secondary));
          border: 3px solid #fff;
          border-radius: 50%;
          width: ${size}px;
          height: ${size}px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          font-weight: 700;
          font-size: ${Math.min(14 + count, 20)}px;
          box-shadow: 0 4px 12px rgba(165, 145, 239, 0.5);
        ">
          ${count}
        </div>
      `,
      className: 'custom-marker-cluster',
      iconSize: [size, size],
    });
  };

  if (locationData.length === 0) {
    return null;
  }

  return (
    <MapWrapper>
      <MapHeader>
        <MapTitle>
          <MapPin size={24} />
          Carte Interactive des Cibles
        </MapTitle>
        <MapStats>
          <StatBadge>
            <Target size={16} />
            {stats.total} cibles
          </StatBadge>
          <div style={{ position: 'relative' }}>
            <StatBadge onClick={() => setShowCityFilter(!showCityFilter)}>
              <MapPin size={16} />
              {stats.locations} villes
            </StatBadge>
            {showCityFilter && (
              <FilterDropdown>
                <FilterTitle>Filtrer par villes</FilterTitle>
                {allLocationData.map((loc, index) => (
                  <FilterItem key={index}>
                    <input
                      type="checkbox"
                      checked={selectedCities.length === 0 || selectedCities.includes(loc.city)}
                      onChange={() => toggleCityFilter(loc.city)}
                    />
                    {loc.city} ({loc.profiles.length})
                  </FilterItem>
                ))}
                <FilterActions>
                  <FilterButton onClick={selectAllCities}>Tout sélectionner</FilterButton>
                  <FilterButton onClick={clearAllCities}>Tout effacer</FilterButton>
                </FilterActions>
              </FilterDropdown>
            )}
          </div>
          <div style={{ position: 'relative' }}>
            <StatBadge onClick={() => setShowCountryFilter(!showCountryFilter)}>
              <Users size={16} />
              {stats.countries} pays
            </StatBadge>
            {showCountryFilter && (
              <FilterDropdown>
                <FilterTitle>Filtrer par pays</FilterTitle>
                {countryStats.map((stat, index) => (
                  <FilterItem key={index}>
                    <input
                      type="checkbox"
                      checked={selectedCountries.length === 0 || selectedCountries.includes(stat.country)}
                      onChange={() => toggleCountryFilter(stat.country)}
                    />
                    <FlagIcon countryCode={getCountryCode(stat.country)} />
                    {stat.country} ({stat.count})
                  </FilterItem>
                ))}
                <FilterActions>
                  <FilterButton onClick={selectAllCountries}>Tout sélectionner</FilterButton>
                  <FilterButton onClick={clearAllCountries}>Tout effacer</FilterButton>
                </FilterActions>
              </FilterDropdown>
            )}
          </div>
        </MapStats>
      </MapHeader>

      <MapContentGrid>
        <MapSection>
          <MapContainer
            center={locationData[0]?.coordinates || [48.8566, 2.3522]}
            zoom={3}
            minZoom={2}
            maxBounds={[[-90, -180], [90, 180]]}
            maxBoundsViscosity={1.0}
            style={{ height: '400px', width: '100%', borderRadius: '8px' }}
          >
            <MapRefHandler setMapRef={setMapRef} />
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
              url="https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png"
            />
            
            <MarkerClusterGroup
              chunkedLoading
              maxClusterRadius={80}
              spiderfyOnMaxZoom={true}
              showCoverageOnHover={false}
              zoomToBoundsOnClick={true}
              disableClusteringAtZoom={12}
              singleMarkerMode={false}
              iconCreateFunction={(cluster) => {
                const markers = cluster.getAllChildMarkers();
                const totalProfiles = markers.reduce((sum, m) => sum + (m.options.profileCount || 0), 0);
                let size = 38;
                let fontSize = 12;
                if (totalProfiles >= 1000) { size = 62; fontSize = 16; }
                else if (totalProfiles >= 500) { size = 56; fontSize = 15; }
                else if (totalProfiles >= 100) { size = 50; fontSize = 14; }
                else if (totalProfiles >= 30) { size = 44; fontSize = 13; }
                else if (totalProfiles >= 10) { size = 40; fontSize = 12; }
                return L.divIcon({
                  html: `<div style="
                    background: linear-gradient(135deg, rgba(165,145,239,0.92), rgba(120,100,200,0.92));
                    border: 3px solid rgba(255,255,255,0.85);
                    border-radius: 50%;
                    width: ${size}px; height: ${size}px;
                    display: flex; align-items: center; justify-content: center;
                    color: #fff; font-weight: 800; font-size: ${fontSize}px;
                    box-shadow: 0 4px 14px rgba(165,145,239,0.5);
                  ">${totalProfiles}</div>`,
                  className: 'custom-cluster-icon',
                  iconSize: [size, size],
                });
              }}
            >
              {locationData.map((location, index) => (
                <Marker
                  key={`loc-${index}`}
                  position={location.coordinates}
                  profileCount={location.profiles.length}
                  icon={createCustomIcon(location.profiles.length)}
                  eventHandlers={{
                    click: () => openLocationModal(location)
                  }}
                >
                  <Popup closeButton={true} autoClose={false} closeOnClick={false}>
                    <PopupContent>
                      <PopupLocation>
                        <MapPin size={14} />
                        {location.city}, {location.country}
                      </PopupLocation>
                      <PopupInfo>
                        <Users size={14} />
                        {location.profiles.length} profil{location.profiles.length > 1 ? 's' : ''}
                      </PopupInfo>
                      <div style={{ marginTop: '8px', maxHeight: '150px', overflowY: 'auto' }}>
                        {location.profiles.slice(0, 5).map((profile, idx) => (
                          <PopupUsername
                            key={idx}
                            href={`https://www.instagram.com/${profile.username}`}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            @{profile.username}
                          </PopupUsername>
                        ))}
                        {location.profiles.length > 5 && (
                          <div style={{ color: 'var(--text-muted)', fontSize: '11px', marginTop: '4px' }}>
                            +{location.profiles.length - 5} autres
                          </div>
                        )}
                      </div>
                    </PopupContent>
                  </Popup>
                </Marker>
              ))}
            </MarkerClusterGroup>
          </MapContainer>
        </MapSection>
      </MapContentGrid>

      {showModal && modalLocation && (
        <ModalOverlay onClick={closeModal}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>
                <MapPin size={24} />
                {modalLocation.city}, {modalLocation.country}
                <span style={{ color: 'var(--text-secondary)', fontSize: '14px', fontWeight: '400' }}>
                  ({modalLocation.profiles.length} profil{modalLocation.profiles.length > 1 ? 's' : ''})
                </span>
              </ModalTitle>
              <CloseButton onClick={closeModal}>
                <X size={20} />
              </CloseButton>
            </ModalHeader>

            <ModalBody>
              {/* Filtre par ville si plusieurs villes disponibles */}
              {modalLocation.allLocations && modalLocation.allLocations.length > 1 && (
                <div style={{ marginBottom: '20px', padding: '16px', background: 'var(--card-bg)', borderRadius: '8px' }}>
                  <div style={{ color: 'var(--text-secondary)', fontSize: '14px', fontWeight: '600', marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <MapPin size={16} />
                    Filtrer par ville
                  </div>
                  <input
                    type="text"
                    placeholder="Rechercher une ville..."
                    value={modalCityFilter}
                    onChange={(e) => setModalCityFilter(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      background: 'var(--bg)',
                      border: '1px solid var(--card-border)',
                      borderRadius: '6px',
                      color: 'var(--btn-text)',
                      fontSize: '14px',
                      marginBottom: '12px'
                    }}
                  />
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                    {modalLocation.allLocations
                      .filter(loc => !modalCityFilter || loc.city.toLowerCase().includes(modalCityFilter.toLowerCase()))
                      .map((loc, idx) => (
                        <label
                          key={idx}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px',
                            padding: '8px 12px',
                            background: modalSelectedCities.includes(loc.city) ? 'var(--primary)' : 'var(--surface)',
                            border: `1px solid ${modalSelectedCities.includes(loc.city) ? 'var(--primary)' : 'var(--card-border)'}`,
                            borderRadius: '6px',
                            cursor: 'pointer',
                            fontSize: '13px',
                            color: 'var(--btn-text)',
                            transition: 'all 0.2s ease'
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={modalSelectedCities.includes(loc.city)}
                            onChange={() => {
                              setModalSelectedCities(prev => 
                                prev.includes(loc.city)
                                  ? prev.filter(c => c !== loc.city)
                                  : [...prev, loc.city]
                              );
                            }}
                            style={{ cursor: 'pointer' }}
                          />
                          {loc.city} ({loc.profiles.length})
                        </label>
                      ))}
                  </div>
                  <div style={{ marginTop: '12px', display: 'flex', gap: '8px' }}>
                    <ActionButton onClick={() => setModalSelectedCities(modalLocation.allLocations.map(loc => loc.city))}>
                      Toutes les villes
                    </ActionButton>
                    <ActionButton onClick={() => setModalSelectedCities([])}>
                      Aucune ville
                    </ActionButton>
                  </div>
                </div>
              )}

              <div style={{ marginBottom: '16px', display: 'flex', gap: '8px', alignItems: 'center' }}>
                <ActionButton onClick={selectAllProfiles}>
                  <Check size={16} />
                  Tout sélectionner
                </ActionButton>
                <ActionButton onClick={deselectAllProfiles}>
                  <X size={16} />
                  Tout désélectionner
                </ActionButton>
                <span style={{ color: 'var(--text-secondary)', fontSize: '13px', marginLeft: 'auto' }}>
                  {(() => {
                    const filteredProfiles = modalSelectedCities.length > 0
                      ? modalLocation.profiles.filter(p => 
                          modalLocation.allLocations?.some(loc => 
                            loc.city && modalSelectedCities.includes(loc.city) && loc.profiles.some(lp => lp.username === p.username)
                          ) || (!p.location_city && modalSelectedCities.includes('Sans ville'))
                        )
                      : modalLocation.profiles;
                    return `${filteredProfiles.length} profil${filteredProfiles.length > 1 ? 's' : ''} affiché${filteredProfiles.length > 1 ? 's' : ''}`;
                  })()}
                </span>
              </div>

              {(() => {
                const filteredProfiles = modalSelectedCities.length > 0
                  ? modalLocation.profiles.filter(p => 
                      modalLocation.allLocations?.some(loc => 
                        loc.city && modalSelectedCities.includes(loc.city) && loc.profiles.some(lp => lp.username === p.username)
                      ) || (!p.location_city && modalSelectedCities.includes('Sans ville'))
                    )
                  : modalLocation.profiles;
                
                return filteredProfiles.map((profile, index) => (
                <ProfileItem key={index}>
                  <ProfileCheckbox
                    type="checkbox"
                    checked={selectedProfiles.some(p => p.username === profile.username)}
                    onChange={() => toggleProfileSelection(profile)}
                  />
                  <ProfileInfo>
                    <ProfileUsername
                      href={`https://www.instagram.com/${profile.username}`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      @{profile.username}
                    </ProfileUsername>
                    <ProfileDetails>
                      {profile.full_name && <span>{profile.full_name}</span>}
                      {profile.followers_count && (
                        <span>👥 {profile.followers_count.toLocaleString()} followers</span>
                      )}
                      {profile.following_count && (
                        <span>👤 {profile.following_count.toLocaleString()} following</span>
                      )}
                    </ProfileDetails>
                  </ProfileInfo>
                </ProfileItem>
                ));
              })()}
            </ModalBody>

            <ModalFooter>
              <ActionButton onClick={closeModal}>
                Annuler
              </ActionButton>
              <ActionButton 
                $primary 
                onClick={addAsTargets}
                disabled={selectedProfiles.length === 0}
              >
                <UserPlus size={16} />
                Ajouter comme cibles ({selectedProfiles.length})
              </ActionButton>
            </ModalFooter>
          </ModalContent>
        </ModalOverlay>
      )}
    </MapWrapper>
  );
}

export default MapView;
